/**
 * The one-line sentence that reports a server-evaluated rule (SE-8458).
 *
 * This was `complianceLayers.ts`'s private `readingPair`/`summaryFor` pair,
 * serving the Compliance tab's layer board alone. SE-8458 gave the action
 * modals' Live Impact panel the same server evaluation, and SE-8458's own
 * acceptance criteria require the two surfaces to report the SAME squad cost
 * ratio for the same scenario. Two copies of this wording would be two things
 * that can drift, so there is one, and both surfaces call it.
 *
 * Every branch is a report of what the SERVER said. The unmeasured states get
 * their own wording so a reader never mistakes "nobody entered this" for "this
 * is fine" — the confusion PRD 5.4 calls out.
 *
 * The parameter is a STRUCTURAL shape rather than a generated query type on
 * purpose: the two callers select different field sets from the same server
 * type, and pinning this to either one would force the other to over-select.
 * @module features/financial-compliance/domain/ruleSummary
 */

import { formatReading } from "./complianceUnits";
import { isNotVerifiable } from "./squadCostEvaluation";

import {
  ComplianceRuleState,
  type CompliancePartialReason,
  type ComplianceQuantityUnit,
} from "@/generated/graphql";

/**
 * The fields a rule must carry to be described in one line.
 *
 * Deliberately the minimum: a caller that selects more is still accepted, and a
 * caller that selects less will not compile.
 */
export interface RuleSummarySource {
  /** The rule's name, as the server registered it. */
  readonly ruleName: string;
  /** The verdict the server reached. */
  readonly state: ComplianceRuleState;
  /** The measured value, or null when the server could not measure it. */
  readonly value?: number | null;
  /** The limit, or null when no limit is configured. */
  readonly threshold?: number | null;
  /** What both figures are denominated in. */
  readonly unit: ComplianceQuantityUnit;
  /**
   * Why a PARTIAL rule withheld a verdict (SE-8798). Optional: a caller that
   * does not select it keeps the plain "awaiting figures" wording.
   */
  readonly partialReasons?: readonly CompliancePartialReason[];
  /** The figures a not-verifiable rule needs, as the backend names them (SE-8798). */
  readonly unverifiableFigures?: readonly string[];
}

/**
 * The sentence for a rule the app cannot verify (SE-8798) — "not verifiable",
 * never "awaiting figures", which would read as the club having forgotten a
 * figure it has no field for.
 * @param ruleName - The rule's name.
 * @param figures - The figures it needs, as the backend names them.
 * @returns The sentence.
 */
const notVerifiableSummaryFor = (
  ruleName: string,
  figures: readonly string[]
): string =>
  figures.length === 0
    ? `${ruleName} — not verifiable`
    : `${ruleName} — not verifiable (needs ${figures.join(", ")})`;

/**
 * The "reading / limit" pair, or null when either half is missing.
 *
 * Both halves or neither: a reading printed beside an absent limit reads as a
 * verdict against a limit nobody set.
 * @param source - The rule.
 * @returns The pair, or null.
 */
export const readingPairFor = (source: RuleSummarySource): string | null => {
  const { value, threshold, unit } = source;
  if (value == null || threshold == null) return null;
  return `${formatReading(value, unit)} / ${formatReading(threshold, unit)}`;
};

/**
 * The one-line sentence under a rule's name.
 * @param source - The rule.
 * @returns The sentence.
 */
export const ruleSummaryFor = (source: RuleSummarySource): string => {
  const { ruleName, state } = source;
  if (isNotVerifiable({ state, partialReasons: source.partialReasons ?? [] })) {
    return notVerifiableSummaryFor(ruleName, source.unverifiableFigures ?? []);
  }
  if (state === ComplianceRuleState.NotConfigured) {
    return `${ruleName} — not configured`;
  }
  if (state === ComplianceRuleState.Partial) {
    return `${ruleName} — awaiting figures`;
  }
  const pair = readingPairFor(source);
  if (pair == null) return `${ruleName} — awaiting figures`;
  return `${ruleName} — ${pair}`;
};
